import {
  buildStoragePath,
  parseStoragePath,
  pathBelongsTo,
  signedUrlSeconds,
  uploadContractSchema,
  type StorageBucket,
} from "@base-hormonal/contracts";
import { PublicAppError } from "../lib/errors";
import { validateUploadFile } from "../lib/media";
import { withRequestSignal } from "../lib/request";
import type { TypedSupabaseClient } from "./supabase";

export interface UploadRequest {
  bucket: StorageBucket;
  /** Segmentos UUID que precedem o nome: conta, perfil e, no corpo, avaliação. */
  segments: readonly string[];
  file: File;
  /** Limite adicional vindo de `account_entitlements`, nunca acima do bucket. */
  maxBytes?: number | null;
}

export interface UploadedObject {
  bucket: StorageBucket;
  path: string;
  sizeBytes: number;
  contentType: string;
}

export interface StorageTelemetry {
  (event: { action: string; domain: string; storageDeltaBytes?: number; errorCode?: string }): void;
}

function objectExtension(contentType: string): string {
  return (
    {
      "image/jpeg": "jpg",
      "image/png": "png",
      "image/webp": "webp",
      "application/pdf": "pdf",
    }[contentType] ?? "bin"
  );
}

/**
 * Único ponto de contato com os quatro buckets privados. Centraliza validação
 * de path, validação de arquivo, URL assinada com expiração e a compensação do
 * objeto órfão quando a escrita do metadado falha depois do upload.
 *
 * Nenhum método publica objeto, e nenhuma telemetria carrega nome, path,
 * conteúdo ou imagem — apenas ação, domínio, bytes e código de erro.
 */
export function createStorageService(client: TypedSupabaseClient, telemetry?: StorageTelemetry) {
  function report(event: Parameters<StorageTelemetry>[0]): void {
    try {
      telemetry?.(event);
    } catch {
      /* telemetria nunca derruba o fluxo do usuário */
    }
  }

  return {
    buildPath(bucket: StorageBucket, segments: readonly string[], objectName: string): string {
      return buildStoragePath(bucket, segments, objectName);
    },

    isOwnPath(bucket: StorageBucket, path: string, userId: string, profileId?: string): boolean {
      return pathBelongsTo(bucket, path, userId, profileId);
    },

    async upload(request: UploadRequest, domain: string): Promise<UploadedObject> {
      const validated = await validateUploadFile(request.bucket, request.file, request.maxBytes);
      const path = buildStoragePath(
        request.bucket,
        request.segments,
        `${crypto.randomUUID()}.${objectExtension(validated.contentType)}`,
      );
      const contract = uploadContractSchema.safeParse({
        bucket: request.bucket,
        path,
        contentType: validated.contentType,
        sizeBytes: validated.sizeBytes,
      });
      if (!contract.success) {
        throw new PublicAppError("upload_contract", "O envio não atende às regras deste arquivo.");
      }
      const { error } = await client.storage
        .from(request.bucket)
        .upload(path, request.file, { contentType: validated.contentType, upsert: false });
      if (error !== null) {
        report({ action: "upload_failed", domain, errorCode: "storage_upload" });
        throw new PublicAppError("storage_failed", "Não foi possível guardar o arquivo privado.");
      }
      report({ action: "upload", domain, storageDeltaBytes: validated.sizeBytes });
      return {
        bucket: request.bucket,
        path,
        sizeBytes: validated.sizeBytes,
        contentType: validated.contentType,
      };
    },

    /**
     * Envia o arquivo e só então grava o metadado. Se a gravação falhar, o
     * objeto é removido; se a remoção também falhar, o órfão é telemetrizado
     * sem expor o path e o erro público continua sanitizado.
     */
    async uploadThenCommit<T>(
      request: UploadRequest,
      domain: string,
      commit: (uploaded: UploadedObject) => Promise<T>,
    ): Promise<T> {
      const uploaded = await this.upload(request, domain);
      try {
        return await commit(uploaded);
      } catch (error: unknown) {
        const removed = await this.remove(uploaded.bucket, [uploaded.path], domain).then(
          () => true,
          () => false,
        );
        if (!removed) {
          report({
            action: "orphan_object",
            domain,
            storageDeltaBytes: uploaded.sizeBytes,
            errorCode: "compensation_failed",
          });
        }
        throw error;
      }
    },

    async signedUrl(
      bucket: StorageBucket,
      path: string,
      seconds?: number | null,
      signal?: AbortSignal,
    ): Promise<string | null> {
      if (parseStoragePath(bucket, path) === null) return null;
      return withRequestSignal(signal, async () => {
        const { data, error } = await client.storage
          .from(bucket)
          .createSignedUrl(path, signedUrlSeconds(bucket, seconds));
        if (error !== null || data === null) return null;
        return data.signedUrl;
      });
    },

    /** Um lote por bucket; paths malformados são descartados antes da chamada. */
    async signedUrls(
      bucket: StorageBucket,
      paths: readonly string[],
      seconds?: number | null,
      signal?: AbortSignal,
    ): Promise<Map<string, string>> {
      const valid = [...new Set(paths)].filter((path) => parseStoragePath(bucket, path) !== null);
      if (valid.length === 0) return new Map();
      return withRequestSignal(signal, async () => {
        const { data, error } = await client.storage
          .from(bucket)
          .createSignedUrls(valid, signedUrlSeconds(bucket, seconds));
        const urls = new Map<string, string>();
        if (error !== null || data === null) return urls;
        for (const entry of data) {
          const path: unknown = entry.path;
          const signed: unknown = entry.signedUrl;
          if (entry.error === null && typeof path === "string" && typeof signed === "string") {
            urls.set(path, signed);
          }
        }
        return urls;
      });
    },

    async remove(bucket: StorageBucket, paths: readonly string[], domain: string): Promise<void> {
      const valid = paths.filter((path) => parseStoragePath(bucket, path) !== null);
      if (valid.length === 0) return;
      const { error } = await client.storage.from(bucket).remove([...valid]);
      if (error !== null) {
        report({ action: "remove_failed", domain, errorCode: "storage_remove" });
        throw new PublicAppError("storage_failed", "Não foi possível remover o arquivo privado.");
      }
      report({ action: "remove", domain });
    },

    /** Remoção best-effort: o metadado já foi apagado e o histórico não volta. */
    async removeQuietly(
      bucket: StorageBucket,
      paths: readonly string[],
      domain: string,
    ): Promise<void> {
      await this.remove(bucket, paths, domain).catch(() => {
        report({ action: "orphan_object", domain, errorCode: "compensation_failed" });
      });
    },
  };
}

export type StorageService = ReturnType<typeof createStorageService>;
